class SiteHeader extends HTMLElement {
  constructor() {
    super();
  }

  selectors = {
    wrapper: ".shopify-section--header",
    search: {
      wrapper: "[data-search-wrapper]",
      button: "[data-search-button]",
      input: "[data-search-input]"
    }
  };

  get _scrollUpThreshold() {
    return 50;
  }

  get _elementHeight() {
    return Math.round(this.offsetHeight);
  }

  get isSticky() {
    return this.getAttribute("is-sticky") === "true";
  }

  get isHidden() {
    return this.getAttribute("is-hidden") === "true";
  }

  connectedCallback() {
    this._saveElementHeight(this._elementHeight);

    if (this.isSticky) {
      this._initSticky();
    }

    this._initSearch();

    window.addEventListener("scroll", throttle(this._onScroll.bind(this)));
    window.addEventListener("resize", throttle(this._onResize.bind(this)));
  }

  _onScroll() {
    this._saveElementHeight(this._elementHeight);

    if (this.isSticky) {
      const currentScrollPosition = window.scrollY;
      const headerBottomThreshold = this._elementHeight;

      const isWrapperSticky = this._wrapperElement.getAttribute("is-sticky") === "true";
      const shouldWrapperBeSticky = currentScrollPosition > this._wrapperElementBottomPosition;

      if (currentScrollPosition < this._lastScrollPosition) {
        if (shouldWrapperBeSticky !== isWrapperSticky) {
          this._wrapperElement.setAttribute("is-sticky", "true");
        }
      }

      if (currentScrollPosition < this._wrapperElementTopPosition) {
        this._wrapperElement.setAttribute("is-sticky", "false");
      }

      if (currentScrollPosition < 0) {
        return;
      }

      if (currentScrollPosition <= headerBottomThreshold) {
        this._scrolledDistance = 0;
        this._show();

        this._lastScrollPosition = currentScrollPosition;

        return;
      }

      if (currentScrollPosition > this._lastScrollPosition) {
        this._scrolledDistance += currentScrollPosition - this._lastScrollPosition;
      } else {
        this._scrolledDistance = 0;
        this._show();
      }

      if (this._scrolledDistance > this._scrollUpThreshold) {
        this._hide();
      }

      this._lastScrollPosition = currentScrollPosition;
    }
  }

  _onResize() {
    this._saveElementHeight(this._elementHeight);

    if (this.isSticky) {
      this._wrapperElementTopPosition = this._wrapperElement.offsetTop;
      this._wrapperElementBottomPosition = this._wrapperElement.offsetTop + this._wrapperElement.offsetHeight;
    }
  }

  _initSticky() {
    this._lastScrollPosition = 0;
    this._scrolledDistance = 0;

    this._wrapperElement = this.closest(this.selectors.wrapper);
    this._wrapperElementTopPosition = this._wrapperElement.offsetTop;
    this._wrapperElementBottomPosition = this._wrapperElement.offsetTop + this._wrapperElement.offsetHeight;
  }

  _initSearch() {
    this._searchWrapper = this.querySelector(this.selectors.search.wrapper);
    this._searchButton = this.querySelector(this.selectors.search.button);
    this._searchInput = this.querySelector(this.selectors.search.input);

    if (!this._searchWrapper) {
      return;
    }

    this._searchButton.addEventListener("click", this._onSearchButtonClick.bind(this));
    document.addEventListener("click", this._onOutsideSearchClick.bind(this));
  }

  _onSearchButtonClick(event) {
    event.stopPropagation();

    const isSearchButtonExpanded = this._searchButton.getAttribute("aria-expanded") === "true";
    this._searchButton.setAttribute("aria-expanded", !isSearchButtonExpanded);

    if (!isSearchButtonExpanded) {
      this._searchInput.focus();
    } else {
      this._searchInput.blur();
    }
  }

  _onOutsideSearchClick(event) {
    if (!this._searchWrapper.contains(event.target)) {
      this._searchButton.setAttribute("aria-expanded", "false");
      this._searchInput.blur();
    }
  }

  _show() {
    this.setAttribute("is-hidden", "false");
  }

  _hide() {
    this.setAttribute("is-hidden", "true");
  }

  _saveElementHeight(value) {
    document.documentElement.style.setProperty(
      '--header-height',
      `${value}px`
    );
  }
}

if (!customElements.get("site-header")) {
  customElements.define("site-header", SiteHeader);
}

class HeaderSidebar extends HTMLElement {
  selectors = {
    openButton: "[data-header-sidebar-open]",
    closeButton: "[data-header-sidebar-close]",
    scrollableContent: "[data-scrollable]",
    tieredMenu: {
      container: "[data-tiered-menu]",
      openButton: "[data-tiered-menu-trigger]",
      backButton: "[data-tiered-menu-back-button]"
    }
  };

  modifiers = {
    sidebar: {
      open: "drawer--open"
    },
    tieredMenu: {
      open: "header-sidebar__navigation-tier--open"
    }
  };

  constructor() {
    super();
  }

  connectedCallback() {
    this._saveDrawerHeight(window.innerHeight);

    this._scrollableContent = this.querySelector(this.selectors.scrollableContent);

    this._initNavigation();
    this._initTieredMenus();

    document.addEventListener("navigation:open", this._openNavigation.bind(this));
    document.addEventListener("navigation:close", this._closeNavigation.bind(this));

    window.addEventListener("resize", throttle(this._onResize.bind(this)));
  }

  _onResize() {
    this._saveDrawerHeight(window.innerHeight);
  }

  _initNavigation() {
    document.addEventListener("click", (event) => {
      const openButton = event.target.closest(this.selectors.openButton);
      if (openButton) {
        return this._openNavigation(event);
      }

      const closeButton = event.target.closest(this.selectors.closeButton);
      if (closeButton) {
        return this._closeNavigation(event);
      }
    });
  }

  _openNavigation(event) {
    event.preventDefault();

    this.setAttribute("aria-expanded", "true");
    this.removeAttribute("inert");

    bodyScrollLock.disableBodyScroll(this._scrollableContent);

    this.classList.add(this.modifiers.sidebar.open);
  }

  _closeNavigation(event) {
    event.preventDefault();

    this.setAttribute("aria-expanded", "false");
    this.setAttribute("inert", "");

    this.classList.remove(this.modifiers.sidebar.open);

    this._closeAllTieredMenus();

    bodyScrollLock.enableBodyScroll(this._scrollableContent);
  }

  _initTieredMenus() {
    document.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        this._openTieredMenu(event);
      }
    });

    document.addEventListener("click", (event) => {
      const tieredMenuOpenButton = event.target.closest(this.selectors.tieredMenu.openButton);
      if (tieredMenuOpenButton) {
        return this._openTieredMenu(event);
      }

      const tieredMenuBackButton = event.target.closest(this.selectors.tieredMenu.backButton);
      if (tieredMenuBackButton) {
        return this._closeTieredMenu(event);
      }
    });
  }

  _openTieredMenu(event) {
    const openButton = event?.target.closest(this.selectors.tieredMenu.openButton);

    if (!openButton) {
      return;
    }

    const tieredMenu = openButton.nextElementSibling;

    if (!tieredMenu) {
      return;
    }

    bodyScrollLock.disableBodyScroll(tieredMenu);

    tieredMenu.classList.add(this.modifiers.tieredMenu.open);

    tieredMenu.setAttribute("aria-expanded", "true");
    tieredMenu.removeAttribute("inert");
  }

  _closeTieredMenu(event) {
    const backButton = event?.target.closest(this.selectors.tieredMenu.backButton);

    if (!backButton) {
      return;
    }

    const tieredMenu = backButton.closest(this.selectors.tieredMenu.container);

    if (!tieredMenu) {
      return;
    }

    tieredMenu.classList.remove(this.modifiers.tieredMenu.open);

    bodyScrollLock.enableBodyScroll(tieredMenu);

    tieredMenu.setAttribute("aria-expanded", "false");
    tieredMenu.setAttribute("inert", "");
  }

  _closeAllTieredMenus() {
    const tieredMenus = [
      ...this.querySelectorAll(this.selectors.tieredMenu.container)
    ];

    if (!tieredMenus.length) {
      return false;
    }

    tieredMenus.forEach((tieredMenu) => {
      tieredMenu.classList.remove(this.modifiers.tieredMenu.open);

      tieredMenu.setAttribute("aria-expanded", "false");
      tieredMenu.setAttribute("inert", "");

      bodyScrollLock.enableBodyScroll(tieredMenu);
    });
  }

  _saveDrawerHeight(value) {
    this.style.setProperty(
      '--drawer-height',
      `${value}px`
    );
  }
}

if (!customElements.get("header-sidebar")) {
  customElements.define("header-sidebar", HeaderSidebar);
}
